import React, { useCallback } from "react";
import { AgGrid, AgGridReact, EmptyState, Flex, Grid } from "@jsq/ds/core";
import { usePaymentBatchDataContext } from "../../../Context/context";
import { PositionRowData } from "../../../types";
import { getColumnDefinitions } from "./columnDefinitions";

export type SelectPositionTableProps = {
  allowSelection: boolean;
  emptyTableMessage: string;
  handleSelectionChange?: (selectedRows: PositionRowData[]) => void;
  loading: boolean;
  positionRowData: PositionRowData[];
  showStatusColumn?: boolean;
};

const defaultColumnDef: AgGrid.GridOptions["defaultColDef"] = {
  flex: 1,
  minWidth: 100,
  editable: false,
};

const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
  enableCellTextSelection: true,
  suppressCellFocus: true,
  suppressContextMenu: true,
  rowMultiSelectWithClick: true,
};

export const SelectPositionsTable: React.FC<SelectPositionTableProps> = ({
  allowSelection,
  emptyTableMessage,
  handleSelectionChange,
  loading,
  positionRowData,
  showStatusColumn = false,
}: SelectPositionTableProps) => {
  const { selectedPositions } = usePaymentBatchDataContext();

  if (allowSelection) {
    gridOptions.rowSelection = "multiple";
  } else {
    gridOptions.rowSelection = undefined;
  }

  const onSelectionChange = useCallback(
    (event: AgGrid.SelectionChangedEvent<PositionRowData>) => {
      if (allowSelection && handleSelectionChange) {
        const selectedRows = event.api.getSelectedRows();
        handleSelectionChange(selectedRows);
      }
    },
    [allowSelection, handleSelectionChange],
  );

  const onFirstDataRendered = useCallback(
    ({ api }: AgGrid.FirstDataRenderedEvent<PositionRowData>) => {
      if (allowSelection) {
        const positionIdSet = new Set(selectedPositions.map((p) => p.positionId));
        api.forEachNode((node) => {
          if (positionIdSet.size > 0) {
            if (node.data?.positionId && positionIdSet.has(node.data.positionId)) {
              node.setSelected(true);
            }
          } else {
            if (!node.data?.paymentInstructionsRecentlyUpdated) {
              node.setSelected(true);
            }
          }
        });
      }
    },
    [allowSelection, selectedPositions],
  );

  const onGridReady = useCallback(
    ({ api }: AgGrid.GridReadyEvent) => {
      if (loading) {
        api.showLoadingOverlay();
      }
    },
    [loading],
  );

  return (
    <>
      {!loading && positionRowData.length === 0 ? (
        <Flex>
          <EmptyState illustration="man-with-new" title={emptyTableMessage} />
        </Flex>
      ) : (
        <Flex data-testid="positionsTable">
          <Grid<PositionRowData>
            suppressMovableColumns
            suppressMultiSort
            rowData={loading ? null : positionRowData}
            onGridReady={onGridReady}
            gridOptions={gridOptions}
            columnDefs={getColumnDefinitions(allowSelection, showStatusColumn)}
            defaultColDef={defaultColumnDef}
            pagination={true}
            paginationPageSize={50}
            paginationAutoPageSize={false}
            paginationPageSizeSelector={false}
            domLayout="autoHeight"
            onSelectionChanged={onSelectionChange}
            onFirstDataRendered={onFirstDataRendered}
            tooltipShowDelay={0}
          />
        </Flex>
      )}
    </>
  );
};
